"use client";

import { Box, Flex } from "@chakra-ui/react";
import { useRef } from "react";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi2";
import { useThemeTokens } from "@/lib/theme/tokens";
import { IconButton } from "./IconButton";
import { scrollPaginationToTableTop } from "./pagination-scroll";
import { Text } from "./Text";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  canGoPrev: boolean;
  canGoNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  totalCount?: number;
  rangeStart?: number;
  rangeEnd?: number;
  ariaLabel?: string;
  mt?: number | string;
  /** Narrow containers (e.g. modals on mobile): single row, shorter labels */
  compact?: boolean;
  /** Root nav selector; pagination controls use `-prev-page` / `-next-page` */
  "data-testid"?: string;
  borderColor?: string;
}

export const Pagination = ({
  currentPage,
  totalPages,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  totalCount,
  rangeStart,
  rangeEnd,
  ariaLabel = "Pagination",
  mt = 0,
  compact = false,
  "data-testid": paginationTestId,
  borderColor,
}: PaginationProps) => {
  const theme = useThemeTokens();
  const navRef = useRef<HTMLDivElement>(null);

  const handlePrev = () => {
    if (!canGoPrev) return;
    onPrev();
    scrollPaginationToTableTop(navRef.current);
  };

  const handleNext = () => {
    if (!canGoNext) return;
    onNext();
    scrollPaginationToTableTop(navRef.current);
  };

  if (totalPages <= 1) return null;

  const showRange =
    totalCount != null && totalCount > 0 && rangeEnd != null && rangeEnd > 0;

  const controlSize = compact ? "36px" : { base: "36px", sm: "40px" };

  return (
    <Flex
      ref={navRef}
      as="nav"
      aria-label={ariaLabel}
      {...(paginationTestId ? { "data-testid": paginationTestId } : {})}
      justify="center"
      w="100%"
      maxW="100%"
      mt={mt}
    >
      <Flex
        align="center"
        gap={compact ? 1 : { base: 1, sm: 3 }}
        flexWrap="nowrap"
        justifyContent="center"
        w="100%"
        maxW="100%"
        px={compact ? 2 : { base: 2, sm: 5 }}
        py={2}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor || theme.borderGlass.default}
        bg={theme.bg.card}
        boxShadow={
          borderColor ? "none" : "0 1px 0 rgba(255, 255, 255, 0.06) inset"
        }
      >
        <IconButton
          {...(paginationTestId
            ? { "data-testid": `${paginationTestId}-prev-page` }
            : {})}
          aria-label="Previous page"
          icon={<HiOutlineChevronLeft size={compact ? 16 : 18} />}
          size="sm"
          variant="ghost"
          borderRadius="lg"
          h={controlSize}
          minW={controlSize}
          flexShrink={0}
          onClick={handlePrev}
          isDisabled={!canGoPrev}
          color={theme.text.secondary}
          _hover={{ bg: theme.accent.primaryBg, color: theme.accent.primary }}
          _disabled={{ opacity: 0.35, cursor: "not-allowed" }}
        />

        <Flex
          direction="column"
          align="center"
          justify="center"
          flex={compact ? 1 : { base: 1, sm: "none" }}
          minW={0}
          px={compact ? 1 : { base: 1, sm: 2 }}
          py={0.5}
        >
          <Text
            fontSize={compact ? "xs" : { base: "xs", sm: "sm" }}
            fontWeight="600"
            color={theme.text.primary}
            letterSpacing="-0.01em"
            whiteSpace="nowrap"
            textAlign="center"
          >
            {compact ? (
              <>
                {currentPage} / {totalPages}
              </>
            ) : (
              <>
                <Box as="span" display={{ base: "none", sm: "inline" }}>
                  Page {currentPage} of {totalPages}
                </Box>
                <Box as="span" display={{ base: "inline", sm: "none" }}>
                  {currentPage} / {totalPages}
                </Box>
              </>
            )}
          </Text>
          {showRange && (
            <Text
              fontSize="xs"
              color={theme.text.tertiary}
              mt={0.5}
              whiteSpace="nowrap"
              textAlign="center"
            >
              {compact ? (
                <>
                  {rangeStart}–{rangeEnd} of {totalCount}
                </>
              ) : (
                <>
                  <Box as="span" display={{ base: "none", sm: "inline" }}>
                    Showing {rangeStart}–{rangeEnd} of {totalCount}
                  </Box>
                  <Box as="span" display={{ base: "inline", sm: "none" }}>
                    {rangeStart}–{rangeEnd} of {totalCount}
                  </Box>
                </>
              )}
            </Text>
          )}
        </Flex>

        <IconButton
          {...(paginationTestId
            ? { "data-testid": `${paginationTestId}-next-page` }
            : {})}
          aria-label="Next page"
          icon={<HiOutlineChevronRight size={compact ? 16 : 18} />}
          size="sm"
          variant="ghost"
          borderRadius="lg"
          h={controlSize}
          minW={controlSize}
          flexShrink={0}
          onClick={handleNext}
          isDisabled={!canGoNext}
          color={theme.text.secondary}
          _hover={{ bg: theme.accent.primaryBg, color: theme.accent.primary }}
          _disabled={{ opacity: 0.35, cursor: "not-allowed" }}
        />
      </Flex>
    </Flex>
  );
};
